import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import './OrderManager.css';

interface OrderItem {
  id: string;
  product_id: string;
  quantity: number;
  price: number;
}

interface Order {
  id: string;
  total_amount: number;
  status: string;
  payment_status: string;
  shipping_status: string;
  customer_email: string;
  customer_name: string;
  shipping_address: {
    firstName: string;
    lastName: string;
    street: string;
    houseNumber: string;
    postalCode: string;
    city: string;
    country: string;
  };
  created_at: string;
  shipped_at?: string;
  tracking_number?: string;
  order_items?: OrderItem[];
}

interface OrderCounts {
  pending: number;
  processing: number;
  shipped: number;
  delivered: number;
  cancelled: number;
  totalRevenue: number;
}

const OrderManager: React.FC = () => {
  // single list mode: show all orders, allow optional status filter and search
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [counts, setCounts] = useState<OrderCounts>({
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
    totalRevenue: 0
  });
  const [trackingNumber, setTrackingNumber] = useState('');
  const [updating, setUpdating] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');

  useEffect(() => {
    loadOrders();
    loadCounts();
  }, [filterStatus, searchTerm]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      // Build base query
      let query = supabase.from('orders').select('id, total_amount, shipping_status, payment_status, customer_email, customer_name, created_at, shipped_at, tracking_number');
      if (filterStatus && filterStatus !== 'all') {
        query = query.eq('shipping_status', filterStatus as string);
      }
      // Apply search (order id prefix or customer email contains)
      const trimmed = searchTerm.trim();
      if (trimmed) {
        const idLike = `${trimmed}%`;
        const emailLike = `%${trimmed}%`;
        query = query.or(`id.ilike.${idLike},customer_email.ilike.${emailLike}`);
      }
      query = query.order('created_at', { ascending: false });
      const { data } = await query;
      if (data) setOrders(data as Order[]);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCounts = async () => {
    try {
      // Use Supabase count to avoid transferring all rows
      const pendingRes = await supabase.from('orders').select('id', { count: 'exact' }).in('shipping_status', ['pending', 'processing']).limit(1);
      const shippedRes = await supabase.from('orders').select('id', { count: 'exact' }).eq('shipping_status', 'shipped').limit(1);
      const deliveredRes = await supabase.from('orders').select('id', { count: 'exact' }).eq('shipping_status', 'delivered').limit(1);
      const cancelledRes = await supabase.from('orders').select('id', { count: 'exact' }).eq('shipping_status', 'cancelled').limit(1);
      const revenueRes = await supabase.from('orders').select('total_amount', { count: 'exact' }).eq('payment_status', 'completed');

      const pendingCount = pendingRes.count || 0;
      const shippedCount = shippedRes.count || 0;
      const deliveredCount = deliveredRes.count || 0;
      const cancelledCount = cancelledRes.count || 0;
      const revenueData = revenueRes.data || [];
      const totalRev = revenueData.reduce((sum: number, o: any) => sum + (o.total_amount || 0), 0) || 0;

      setCounts({
        pending: pendingCount,
        processing: 0,
        shipped: shippedCount,
        delivered: deliveredCount,
        cancelled: cancelledCount,
        totalRevenue: totalRev
      });
    } catch (error) {
      console.error('Error loading counts:', error);
    }
  };

  const handleViewOrder = (order: Order) => {
    // Fetch full order details (including order_items) on demand to reduce list payload
    (async () => {
      try {
        const { data, error } = await supabase.from('orders').select('*, order_items(*)').eq('id', order.id).single();
        if (error) throw error;
        setSelectedOrder(data as Order);
        setTrackingNumber(data.tracking_number || '');
        setSelectedStatus(data.shipping_status || 'pending');
        setExpandedOrderId(order.id);
      } catch (err) {
        console.error('Error loading order details:', err);
        // fallback to basic order
        setSelectedOrder(order);
        setTrackingNumber(order.tracking_number || '');
        setSelectedStatus(order.shipping_status || 'pending');
        setExpandedOrderId(order.id);
      }
    })();
  };

  const updateOrderStatus = async (orderId: string, newStatus: string, extras: Record<string, any> = {}) => {
    setUpdating(true);
    try {
      const payload = { shipping_status: newStatus, updated_at: new Date().toISOString(), ...extras };
      await supabase.from('orders').update(payload).eq('id', orderId);
      await loadOrders();
      await loadCounts();
      if (selectedOrder && selectedOrder.id === orderId) {
        const { data } = await supabase.from('orders').select('*, order_items(*)').eq('id', orderId).single();
        setSelectedOrder(data as Order);
      }
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setUpdating(false);
    }
  };

  const advanceStatus = (order: Order) => {
    const seq = ['pending', 'processing', 'shipped', 'delivered'];
    const idx = seq.indexOf(order.shipping_status as string);
    const next = idx >= 0 && idx < seq.length - 1 ? seq[idx + 1] : null;
    if (!next) return;
    // if advancing to shipped from processing, no tracking by default (can add in modal)
    updateOrderStatus(order.id, next);
  };

  const handleStartProcessing = async (order: Order) => {
    await updateOrderStatus(order.id, 'processing');
    setExpandedOrderId(null);
  };

  const handleMarkAsShipped = async () => {
    if (!selectedOrder) return;
    await updateOrderStatus(selectedOrder.id, 'shipped', { shipped_at: new Date().toISOString(), tracking_number: trackingNumber || null });
    setExpandedOrderId(null);
    setTrackingNumber('');
  };

  const handleMarkAsDelivered = async (order: Order) => {
    await updateOrderStatus(order.id, 'delivered');
  };

  const handleCancelOrder = async (order: Order) => {
    if (!(window as any).confirm(`Weet je zeker dat je order ${order.id.slice(0, 8)} wilt annuleren?`)) return;
    setUpdating(true);
    try {
      await supabase.from('orders').update({ shipping_status: 'cancelled', updated_at: new Date().toISOString() }).eq('id', order.id);
      await loadOrders();
      await loadCounts();
      if (expandedOrderId === order.id) setExpandedOrderId(null);
    } catch (error) {
      console.error('Error cancelling order:', error);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatPrice = (price: number) => `€${price.toFixed(2)}`;

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, string> = {
      pending: 'status-pending', processing: 'status-processing',
      shipped: 'status-shipped', delivered: 'status-delivered', cancelled: 'status-cancelled'
    };
    return `status-badge ${statusMap[status] || 'status-default'}`;
  };

  return (
    <div className="order-manager">
      <div className="order-stats">
        <div className="stat-card stat-pending" onClick={() => setFilterStatus('pending')}>
          <span className="stat-number">{counts.pending}</span>
          <span className="stat-label">Te Verwerken</span>
        </div>
        <div className="stat-card stat-shipped" onClick={() => setFilterStatus('shipped')}>
          <span className="stat-number">{counts.shipped}</span>
          <span className="stat-label">Verzonden</span>
        </div>
        <div className="stat-card stat-delivered" onClick={() => setFilterStatus('delivered')}>
          <span className="stat-number">{counts.delivered}</span>
          <span className="stat-label">Afgeleverd</span>
        </div>
        <div className="stat-card stat-revenue">
          <span className="stat-number">{formatPrice(counts.totalRevenue)}</span>
          <span className="stat-label">Totale Omzet</span>
        </div>
      </div>

      <div className="order-tabs">
        <button className={filterStatus === 'pending' ? 'active' : ''} onClick={() => setFilterStatus('pending')}>Te Verwerken ({counts.pending})</button>
        <button className={filterStatus === 'shipped' ? 'active' : ''} onClick={() => setFilterStatus('shipped')}>Verzonden ({counts.shipped})</button>
        <button className={filterStatus === 'delivered' ? 'active' : ''} onClick={() => setFilterStatus('delivered')}>Afgeleverd ({counts.delivered})</button>
      </div>

      <div className="top-controls">
        <input
          type="search"
          placeholder="Zoek op order id of e-mail"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="orders-search"
        />
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as any)} className="orders-filter">
          <option value="all">Alle statussen</option>
          <option value="pending">Te verwerken</option>
          <option value="processing">In behandeling</option>
          <option value="shipped">Verzonden</option>
          <option value="delivered">Afgeleverd</option>
          <option value="cancelled">Geannuleerd</option>
        </select>
      </div>

      <div className="orders-list">
        {loading ? (
          <div className="loading">Orders laden...</div>
        ) : orders.length === 0 ? (
          <div className="no-orders"><p>Geen orders in deze categorie</p></div>
        ) : (
          orders.map(order => (
            <div key={order.id} className={`order-card ticket ${'status-' + (order.shipping_status || 'pending')}`}>
              <div className="order-header">
                <div className="order-id">#{order.id.slice(0, 8).toUpperCase()}</div>
                <div className={getStatusBadge(order.shipping_status)}>
                  {order.shipping_status === 'pending' ? 'Wachten' : order.shipping_status === 'processing' ? 'In behandeling' : order.shipping_status === 'shipped' ? 'Verzonden' : order.shipping_status === 'delivered' ? 'Afgeleverd' : 'Geannuleerd'}
                </div>
              </div>
              <div className="order-info">
                <div className="customer-info">
                  <strong>{order.customer_name}</strong>
                  <span>{order.customer_email}</span>
                </div>
                <div className="order-meta">
                  <span>{formatDate(order.created_at)}</span>
                  <span className="order-total">{formatPrice(order.total_amount)}</span>
                </div>
              </div>
              <div className="order-actions">
                <button className="btn-view" onClick={() => handleViewOrder(order)}>{expandedOrderId === order.id ? 'Toon minder' : 'Bekijken'}</button>
                {(order.shipping_status === 'pending' || order.shipping_status === 'processing') && (
                  <>
                    <button className="btn-process" onClick={() => handleStartProcessing(order)} disabled={updating}>Begin Verwerken</button>
                    <button className="btn-cancel" onClick={() => handleCancelOrder(order)} disabled={updating}>Annuleren</button>
                  </>
                )}
                {order.shipping_status === 'shipped' && (
                  <button className="btn-deliver" onClick={() => handleMarkAsDelivered(order)} disabled={updating}>Markeer Afgeleverd</button>
                )}
              </div>

              {expandedOrderId === order.id && selectedOrder && selectedOrder.id === order.id && (
                <div className="order-expanded">
                  <div className="detail-section">
                    <h3>Status</h3>
                    <div className="status-timeline">
                      <div className={`timeline-step ${selectedOrder.payment_status === 'completed' ? 'completed' : ''}`}><span className="step-icon">💳</span><span className="step-label">Betaald</span></div>
                      <div className={`timeline-step ${['processing', 'shipped', 'delivered'].includes(selectedOrder.shipping_status) ? 'completed' : ''}`}><span className="step-icon">📦</span><span className="step-label">Verwerkt</span></div>
                      <div className={`timeline-step ${selectedOrder.shipping_status === 'shipped' || selectedOrder.shipping_status === 'delivered' ? 'completed' : ''}`}><span className="step-icon">🚚</span><span className="step-label">Verzonden</span></div>
                      <div className={`timeline-step ${selectedOrder.shipping_status === 'delivered' ? 'completed' : ''}`}><span className="step-icon">✅</span><span className="step-label">Afgeleverd</span></div>
                    </div>
                  </div>
                  <div className="detail-section">
                    <h3>Verzendadres</h3>
                    <address>
                      {selectedOrder.shipping_address?.firstName} {selectedOrder.shipping_address?.lastName}<br />
                      {selectedOrder.shipping_address?.street} {selectedOrder.shipping_address?.houseNumber}<br />
                      {selectedOrder.shipping_address?.postalCode} {selectedOrder.shipping_address?.city}<br />
                      {selectedOrder.shipping_address?.country}
                    </address>
                  </div>
                  <div className="detail-section">
                    <h3>Bestelde Items</h3>
                    <table className="items-table">
                      <thead><tr><th>Product</th><th>Aantal</th><th>Prijs</th><th>Subtotaal</th></tr></thead>
                      <tbody>
                        {selectedOrder.order_items?.map((item, idx) => (
                          <tr key={idx}>
                            <td>Product #{item.product_id?.slice(0, 8)}</td>
                            <td>{item.quantity}</td>
                            <td>{formatPrice(item.price)}</td>
                            <td>{formatPrice(item.price * item.quantity)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot><tr><td colSpan={3}><strong>Totaal</strong></td><td><strong>{formatPrice(selectedOrder.total_amount)}</strong></td></tr></tfoot>
                    </table>
                  </div>
                  <div className="detail-section">
                    <h3>Update Status & Verzenden</h3>
                    <div style={{display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap'}}>
                      <select className="status-select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                        <option value="pending">Wachten</option>
                        <option value="processing">In behandeling</option>
                        <option value="shipped">Verzonden</option>
                        <option value="delivered">Afgeleverd</option>
                        <option value="cancelled">Geannuleerd</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Trackingnummer (optioneel)"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        className="tracking-input"
                      />
                      <button className="btn-process" onClick={async () => {
                        if (!selectedOrder) return;
                        const extras: Record<string, any> = {};
                        if (selectedStatus === 'shipped') extras.shipped_at = new Date().toISOString();
                        if (trackingNumber) extras.tracking_number = trackingNumber;
                        await updateOrderStatus(selectedOrder.id, selectedStatus, extras);
                        setExpandedOrderId(null);
                      }} disabled={updating}>{updating ? 'Bezig...' : 'Update'}</button>
                      <button className="btn-close" onClick={() => setExpandedOrderId(null)}>Sluit</button>
                    </div>
                  </div>
                  {selectedOrder.tracking_number && (
                    <div className="detail-section">
                      <h3>Tracking Info</h3>
                      <p>Tracking: <strong>{selectedOrder.tracking_number}</strong></p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OrderManager;
